import styled from "styled-components";
import { ReportDatabaseContext } from "../../../Contexts/ReportDatabaseContext";
import { useState, useContext, useEffect } from "react";
import { HotTopicsContext } from "../../../Contexts/HotTopicsContext";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {
  getDashBoardData,
  calculateTotalHours,
} from "../../../Helpers/ReportDataFunctions";
import { generateDateArray } from "../../../Helpers/TimeFunctions";
import { getAllTeamIdsFromOrgTreeArray } from "../../../Helpers/OrgTreeFunctions";
import ReportSubmissionCheck from "../Graphs/ReportSubmissionsCheck";

const IndividualDashboardWrapper = styled.div`
  width: 100%;
  padding: 0 10px;
  display: grid;
  grid-template-areas:
    "areas areas"
    "time-share stats"
    "in-person involvement"
    "hot-topic hot-topic"
    "report-check report-check";
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  @media screen and (max-width: 500px) {
    grid-template-areas:
      "areas"
      "stats"
      "time-share"
      "in-person"
      "involvement"
      "hot-topic"
      "report-check";
    grid-template-columns: 1fr;
  }
  .time-share {
    grid-area: time-share;
  }
  .involvement {
    grid-area: involvement;
  }
  .stats {
    grid-area: stats;
  }
  .bar-chart {
    width: 100%;
  }
  .in-person {
    grid-area: in-person;
  }
  .areas {
    grid-area: areas;
  }
  .hot-topics {
    grid-area: hot-topic;
  }
`;

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--accent-color-dark);
  margin: 10px 0;
`;

const GraphCard = styled.div`
  border-radius: 10px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0px 0px 15px 3px rgba(197, 197, 197, 0.201);

  h2 {
    text-transform: capitalize;
    margin-bottom: 10px;
    font-size: 15px;
  }
  p {
    align-self: flex-start;
    font-size: 12px;
    font-weight: 300;
  }
`;

export default function OversightDashboard({ dates, selectedNode }: Props) {
  const hotTopics = useContext(HotTopicsContext);
  const [timeShareSeries, setTimeShareSeries] = useState<
    { name: string; data: number[] }[]
  >([{ name: "Hours", data: [0, 0, 0] }]);
  const [involvementSeries, setInvolvementSeries] = useState<number[]>([
    0, 0, 0,
  ]);
  const [inPersonSeries, setInPersonSeries] = useState<number[]>([0, 0]);
  const reportContext = useContext(ReportDatabaseContext);
  const reportData = reportContext.reportData;
  const [reportsMatchingUserAndDate, setReportsMatchingUserAndDate] = useState<
    ReportData[]
  >([]);
  const [barChartsSeries, setBarChartsSeries] = useState<any>([]);
  const [hotTopicsData, setHotTopicsData] = useState<HotTopic[]>([]);

  //set matching reports
  useEffect(() => {
    if (reportData && selectedNode && dates[0] && dates[1]) {
      //get all the teamIds that are children of the selected node
      const teamIds: string[] = getAllTeamIdsFromOrgTreeArray([selectedNode]);
      const dateRangeArray = generateDateArray(dates[0], dates[1]);
      const firstDay = new Date(dateRangeArray[0].startDate);
      const lastDay = new Date(
        dateRangeArray[dateRangeArray.length - 1].endDate
      );
      const reportMatch = reportData.filter((report) => {
        return (
          teamIds.includes(report.teamId) &&
          new Date(report.reportPeriodDate) >= firstDay &&
          new Date(report.reportPeriodDate) <= lastDay
        );
      });
      setReportsMatchingUserAndDate(reportMatch);
    }
  }, [reportData, selectedNode, dates]);

  //extract data from matching reports
  useEffect(() => {
    if (
      reportsMatchingUserAndDate &&
      careAreascategories &&
      dates[0] &&
      dates[1]
    ) {
      const dashbaordData = getDashBoardData(dates, reportsMatchingUserAndDate);

      setInPersonSeries([
        dashbaordData.totalInPersonHours,
        dashbaordData.totalWrittenHours,
      ]);

      setTimeShareSeries([
        {
          name: "Hours",
          data: [
            dashbaordData.totalHours,
            dashbaordData.adminAndMeetingHours,
            dashbaordData.personalDevelopment,
          ],
        },
      ]);

      setInvolvementSeries([
        dashbaordData.volunteerHours,
        dashbaordData.pastoralCareHours,
        dashbaordData.churchStaffHours,
      ]);

      const hotTopicDataForChart = () => {
        let hotTopicsFromContext = [...hotTopics];
        hotTopicsFromContext.filter((topic) => topic.status === "classified");
        //dashbaordData.hotTopics only contains the raw data from reports so it needs to be used to
        //have the keywords of classfied topics accounted in these classified topics and not independantly.
        if (!dashbaordData.hotTopics) return;
        let hotTopicsWithOccurancesFromReports = hotTopicsFromContext.map(
          (topicFromContext) => {
            const total = dashbaordData.hotTopics.reduce(
              (acc: number, hotTopicFromData: any) => {
                if (
                  hotTopicFromData.label.trim().toLowerCase() ===
                    topicFromContext.label.trim().toLowerCase() ||
                  (topicFromContext.keyWords &&
                    topicFromContext.keyWords.some(
                      (hotTopic) =>
                        hotTopic.trim().toLowerCase() ===
                        hotTopicFromData.label.trim().toLowerCase()
                    ))
                ) {
                  return acc + hotTopicFromData.occurrence;
                } else {
                  return acc;
                }
              },
              0
            );
            return {
              ...topicFromContext,
              keyWords: [],
              occurrence: total,
            };
          }
        );
        const noNullArray = hotTopicsWithOccurancesFromReports.filter(
          (topic) => topic.occurrence > 0 && topic.status === "classified"
        );
        const orderedHotTopics = noNullArray.sort((a, b) => {
          return b.occurrence - a.occurrence;
        });

        const MAX_AMOUNT_OF_HOT_TOPICS_TO_SHOW = 8;
        const mostPopular = orderedHotTopics.slice(
          0,
          MAX_AMOUNT_OF_HOT_TOPICS_TO_SHOW
        );
        return mostPopular;
      };

      setHotTopicsData(hotTopicDataForChart() || []);

      const stringDates = dates.map((date) => date.toISOString());
      const seriesFromReport = [
        {
          name: "Crisis",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["crisisHours"]
          ),
        },
        {
          name: "Relationships",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["relationshipHours"]
          ),
        },
        {
          name: "Life Skills",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["lifeSkillsHours"]
          ),
        },
        {
          name: "Trauma",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["traumaHours"]
          ),
        },
        {
          name: "Additional Needs",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["addNeedsHours"]
          ),
        },
        {
          name: "Health",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["healthHours"]
          ),
        },
        {
          name: "Mental Health",
          data: calculateTotalHours(
            generateDateArray(dates[0], dates[1]),
            reportsMatchingUserAndDate,
            ["mentalHealthHours"]
          ),
        },
      ];
      setBarChartsSeries(seriesFromReport);
    }
  }, [reportsMatchingUserAndDate]);
  const timeShareOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        columnWidth: "80%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [
        ["Pastoral", "Care"],
        ["Admin", "& Meetings"],
        ["Personal", "Developement"],
      ],
      labels: {
        show: false,
      },
    },
  };

  const inPersonShareOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    legend: {
      position: "bottom",
    },
    labels: ["Face to Face & Calls", "Text & Email"],
  };

  const involvementOptions: ApexOptions = {
    chart: {
      type: "donut",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    legend: {
      position: "bottom",
    },
    labels: ["Volunteers", "Pastoral Care Staff", "Church Staff"],
  };

  const careAreascategories = generateDateArray(dates[0], dates[1]).map(
    (dateRange) =>
      dateRange.startDate.toLocaleDateString("en-UK", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
  );

  const careAreasOptions: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "100%",
      toolbar: {
        show: false,
      },

      offsetY: 0,
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 500,
        },
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: ["#ffffff"],
      },
    },
    colors: [
      "var(--crisis-color)",
      "var(--relationships-color)",
      "var(--lifeSkills-color)",
      "var(--trauma-color)",
      "var(--additionalNeeds-color)",
      "var(--health-color)",
      "var(--mentalHealth-color)",
    ],
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "90%",
      },
    },
    xaxis: {
      categories: careAreascategories,
    },
  };

  const hotTopicsOptions: ApexOptions = {
    chart: {
      type: "bar",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 0,
        horizontal: true,
        distributed: true,
        barHeight: "80%",
        isFunnel: true,
      },
    },
    colors: [
      "#F44F5E",
      "#E55A89",
      "#D863B1",
      "#CA6CD8",
      "#B57BED",
      "#8D95EB",
      "#62ACEA",
      "#4BC3E6",
    ],
    dataLabels: {
      enabled: true,
      formatter: function (val, opt) {
        return opt.w.globals.labels[opt.dataPointIndex];
      },
      dropShadow: {
        enabled: true,
      },
    },
    xaxis: {
      categories: hotTopicsData.map(
        (topic) => topic.status == "classified" && topic.label
      ),
    },
    legend: {
      show: false,
    },
  };

  const hotTopicSeries = [
    {
      name: "",
      data: hotTopicsData.map(
        (topic) => (topic.status == "classified" && topic.occurrence) || 0
      ),
    },
  ];
  const getStat = (keyName: keyof ReportData) => {
    if (reportsMatchingUserAndDate) {
      const total = reportsMatchingUserAndDate.reduce((acc, report) => {
        return acc + Number(report[keyName] ?? 0);
      }, 0);
      return total;
    }
    return 0;
  };

  return (
    <IndividualDashboardWrapper>
      <GraphCard className="time-share">
        <h2>Time Share</h2>
        <Chart
          className="donut-chart"
          options={timeShareOptions}
          series={timeShareSeries}
          type="bar"
          height={260}
        />
      </GraphCard>
      <GraphCard className="involvement">
        <h2>Involvement Share</h2>
        <Chart
          className="donut-chart"
          options={involvementOptions}
          series={involvementSeries}
          type="donut"
          height={260}
        />
      </GraphCard>
      <GraphCard className="stats">
        <h2>Ministering Situations</h2>
        <p>
          {`Prepare and Enrich Appointements: ${getStat(
            "prepAndEnrichAppointments"
          )}`}
        </p>
        <p> {`Weddings Conducted: ${getStat("weedingsConducted")}`}</p>
        <Line />
        <p> {`Funerals Conducted: ${getStat("funeralsConducted")}`}</p>
        <p> {`DFV Situations: ${getStat("newDFVIncidents")}`}</p>
        <p> {`Hospital Visits: ${getStat("hospitalVisits")}`}</p>
        <Line />
        <p> {`Death By Sucide: ${getStat("sucideDeath")}`}</p>
        <p> {`Death Of A Child Under Ten: ${getStat("childDeath")}`}</p>
        <p> {`Death Of A Still Born: ${getStat("stillBorn")}`}</p>
        <Line />
        <p> {`Flower/Gifts Hampers: ${getStat("giftsFlowers")}`}</p>
        <p> {`Meals: ${getStat("meals")}`}</p>
        <p> {`Kilo of Kindness/Citycare Bags: ${getStat("kiloOfKindnessBags")}`}</p>
      </GraphCard>
      <GraphCard className="in-person">
        <h2>In Person Time</h2>
        <Chart
          className="donut-chart"
          options={inPersonShareOptions}
          series={inPersonSeries}
          type="donut"
          height={260}
        />
      </GraphCard>
      <GraphCard className="areas">
        <h2>Pastoral Care Areas</h2>
        <Chart
          className="bar-chart"
          options={careAreasOptions}
          series={barChartsSeries}
          height={70 + 60 * careAreascategories.length}
          width="100%"
          type="bar"
        />
      </GraphCard>
      <GraphCard className="hot-topics">
        <h2>Hot Topics</h2>
        {hotTopicSeries[0].data[0] > 0 ? (
          <Chart
            className="bar-chart"
            options={hotTopicsOptions}
            series={hotTopicSeries}
            height={350}
            width="100%"
            type="bar"
          />
        ) : (
          <p>No Hot Topics data for this periode</p>
        )}
      </GraphCard>
      <ReportSubmissionCheck
        dates={dates}
        selectedNode={selectedNode}
        relevantReports={reportData}
      />
    </IndividualDashboardWrapper>
  );
}

interface Props {
  dates: Date[];
  selectedNode: OrgTreeItem;
}
